import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

import type { NarrationLine } from "./narrationTimeline";

// ---------------------------------------------------------------------------
// NarrationCaptions — the spoken line, rendered under the video in fullscreen
// Director View (DIR-014).
//
// This is a caption, so it follows caption conventions rather than app-UI ones:
//
//   legibility first — white text on a near-opaque dark plate, which holds its
//     contrast over any frame behind it, plus a text shadow for the edges;
//   no layout shift — the strip reserves its height, so the video never jumps
//     when a line starts or ends and the picture stays rock steady;
//   measured line length — capped around 46 characters per line, close to the
//     BBC subtitle guideline, because a single wide line is hard to track;
//   announced politely — an aria-live region so a screen reader reads each new
//     line without interrupting whatever else it is saying.
//
// When word timings exist (echoes rendered with the composer's narration block)
// the words light up as they are spoken. That highlight is driven straight to
// the DOM from requestVideoFrameCallback: re-rendering React once per word, at
// frame rate, for a purely visual effect would be wasteful.
// ---------------------------------------------------------------------------

/** Reserved height so the video above never reflows. Two lines of caption. */
const RESERVED_HEIGHT_CLASS = "min-h-[4.5rem]";

export function NarrationCaptions({
  line,
  video,
  className,
}: {
  /** The line currently being spoken, or null between lines. */
  line: NarrationLine | null;
  /** Player whose clock drives word highlighting. */
  video: HTMLVideoElement | null;
  className?: string;
}) {
  const wordsRef = useRef<HTMLParagraphElement | null>(null);
  const spokenCountRef = useRef(-1);

  const words = line?.words ?? null;

  useEffect(() => {
    spokenCountRef.current = -1;
    if (!video || !words || words.length === 0) return;

    const paint = (mediaTime?: number) => {
      const container = wordsRef.current;
      if (!container) return;
      const seconds = mediaTime ?? video.currentTime;
      if (!Number.isFinite(seconds)) return;

      let spoken = 0;
      while (spoken < words.length && words[spoken].startSeconds <= seconds) {
        spoken += 1;
      }
      if (spoken === spokenCountRef.current) return;
      spokenCountRef.current = spoken;

      const nodes = container.children;
      for (let index = 0; index < nodes.length; index += 1) {
        // Upcoming words stay legible, just quieter — never invisible, so the
        // whole line can always be read ahead of the voice.
        (nodes[index] as HTMLElement).style.opacity =
          index < spoken ? "1" : "0.55";
      }
    };

    const onTimeUpdate = () => paint();
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("seeked", onTimeUpdate);

    let handle: number | null = null;
    const supportsRvfc = "requestVideoFrameCallback" in video;
    if (supportsRvfc) {
      const onFrame = (
        _now: DOMHighResTimeStamp,
        metadata: VideoFrameCallbackMetadata,
      ) => {
        paint(metadata.mediaTime);
        handle = video.requestVideoFrameCallback(onFrame);
      };
      handle = video.requestVideoFrameCallback(onFrame);
    }
    paint();

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("seeked", onTimeUpdate);
      if (supportsRvfc && handle != null) {
        video.cancelVideoFrameCallback(handle);
      }
    };
  }, [video, words]);

  return (
    <div
      className={cn(
        "flex w-full items-center justify-center px-2 pt-2",
        RESERVED_HEIGHT_CLASS,
        className,
      )}
      data-testid="director-view-captions"
    >
      {/* The plate only appears with a line, so an empty strip stays invisible
          while still holding its height. */}
      {line ? (
        <p
          ref={wordsRef}
          dir="auto"
          role="status"
          aria-live="polite"
          className={[
            "max-w-[46ch] rounded-md bg-black/75 px-4 py-2 text-center",
            "text-[clamp(0.95rem,1.5vw,1.4rem)] leading-snug font-medium",
            "text-white [text-shadow:0_1px_3px_rgb(0_0_0_/_0.9)]",
          ].join(" ")}
        >
          {words && words.length > 0
            ? words.map((word, position) => (
                <span
                  key={`${line.index}-${position}`}
                  style={{ opacity: 0.55 }}
                  className="transition-opacity duration-100"
                >
                  {position > 0 ? " " : ""}
                  {word.text}
                </span>
              ))
            : line.text}
        </p>
      ) : null}
    </div>
  );
}
